import React, {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {useDispatch} from 'react-redux';
import {toast} from 'react-toastify';
import {IoChevronDown, IoChevronUp} from 'react-icons/io5';
import {FaExclamationCircle} from 'react-icons/fa';
import "pages/bookings/_bookingDetail.scss";
import {api} from 'api/calls';
import {ConfirmModal, Loader} from 'components';
import {routes} from 'common';
import {BOOKING_STATUS} from "../../common/constants";
import {getPaymentMethodDisplay, getStatusBadgeClass, getStatusDisplayLabel} from "./helpers";
import {formatPrice} from 'common/utils';

// Helper to format time in 12-hour format
function formatTime12Hour(time24) {
  if (!time24) return '';
  const [hours, minutes] = time24.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
}

function BookingDetail() {
  const {id} = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [bookingData, setBookingData] = useState(null);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [expandedUserInfo, setExpandedUserInfo] = useState(true);
  const [expandedBookingInfo, setExpandedBookingInfo] = useState(true);
  const [expandedSlots, setExpandedSlots] = useState(true);
  const [expandedChallengeInfo, setExpandedChallengeInfo] = useState(true);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showDeclineModal, setShowDeclineModal] = useState(false);

  useEffect(() => {
    const fetchBookingData = async () => {
      try {
        setFetchLoading(true);
        const response = await api.getBookingDetail(id);
        setBookingData(response);
      } catch (error) {
        toast.error('Failed to fetch booking data');
        navigate('/bookings');
      } finally {
        setFetchLoading(false);
      }
    };

    if (id) {
      fetchBookingData();
    }
  }, [id, navigate]);

  const handleConfirmBooking = async () => {
    setActionLoading(true);
    try {
      await api.updateBookingStatus(id, {status: BOOKING_STATUS.CONFIRMED});
      toast.success('Booking confirmed successfully!');
      navigate(routes.BOOKINGS)
    } catch (error) {
      console.error(error)
    } finally {
      setActionLoading(false);
      setShowConfirmModal(false);
    }
  };

  const handleDeclineBooking = async () => {
    setActionLoading(true);
    try {
      await api.updateBookingStatus(id, {status: BOOKING_STATUS.DECLINED});
      toast.success('Booking declined successfully!');
      navigate(routes.BOOKINGS)
    } catch (error) {
      console.error(error)
    } finally {
      setActionLoading(false);
      setShowDeclineModal(false);
    }
  };

  const toggleUserInfoExpansion = () => {
    setExpandedUserInfo(!expandedUserInfo);
  };

  const toggleBookingInfoExpansion = () => {
    setExpandedBookingInfo(!expandedBookingInfo);
  };

  const toggleSlotsExpansion = () => {
    setExpandedSlots(!expandedSlots);
  };

  const toggleChallengeInfoExpansion = () => {
    setExpandedChallengeInfo(!expandedChallengeInfo);
  };

  const canUpdateStatus = () => {
    return bookingData?.status === BOOKING_STATUS.PENDING;
  };

  if (!bookingData && !fetchLoading) {
    return (
      <div className="booking-detail-page detail-page">
        <div className="error-container">
          <p>Booking not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="booking-detail-page detail-page">
      {(fetchLoading || actionLoading) && <Loader/>}
      <div className="header">
        <h1>Booking Details</h1>
        <p className="subtitle">View and manage booking information</p>
        {bookingData?.status === BOOKING_STATUS.CANCELLED && bookingData?.cancellation_reason && (
          <div className="cancellation-reason-bar" role="alert">
            <FaExclamationCircle className="cancellation-reason-icon" aria-hidden />
            <div className="cancellation-reason-content">
              <span className="cancellation-reason-label">Cancellation reason</span>
              <p className="cancellation-reason-text">{bookingData.cancellation_reason}</p>
            </div>
          </div>
        )}
        {canUpdateStatus() && (
          <div className="approval-status-bar">
            <div className="approval-status-section">
              <span className="approval-status pending">
                Pending Approval
              </span>
              <div className="status-message">
                This booking is waiting for your approval. You can confirm or decline it.
              </div>
            </div>
            <div className="approval-actions">
              <button
                className="approve-btn"
                disabled={actionLoading}
                onClick={() => setShowConfirmModal(true)}
              >
                Confirm
              </button>
              <button
                className="reject-btn"
                disabled={actionLoading}
                onClick={() => setShowDeclineModal(true)}
              >
                Decline
              </button>
            </div>
          </div>
        )}
      </div>

      {bookingData && (
        <div className="custom-form">
          {/* User Information Section */}
          <div className="section-header">
            <div
              className="section-title-wrapper"
              onClick={toggleUserInfoExpansion}
              style={{cursor: 'pointer'}}
            >
              <button
                type="button"
                className="expand-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleUserInfoExpansion();
                }}
                aria-label={expandedUserInfo ? 'Collapse user information' : 'Expand user information'}
              >
                <span className={`expand-icon ${expandedUserInfo ? 'expanded' : ''}`}>
                  {expandedUserInfo ? <IoChevronUp/> : <IoChevronDown/>}
                </span>
              </button>
              <h2>User Information</h2>
            </div>
          </div>

          {expandedUserInfo && (
            <div className="basic-info-content">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="user_name">
                    Full Name
                  </label>
                  <div className="form-control-static">
                    {bookingData.user?.full_name || 'N/A'}
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="user_email">
                    Email
                  </label>
                  <div className="form-control-static">
                    {bookingData.user?.email || 'N/A'}
                  </div>
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="user_phone">
                  Phone Number
                </label>
                <div className="form-control-static">
                  {bookingData.user?.phone_number || 'N/A'}
                </div>
              </div>
            </div>
          )}

          {/* Booking Information Section */}
          <div className="section-header">
            <div
              className="section-title-wrapper"
              onClick={toggleBookingInfoExpansion}
              style={{cursor: 'pointer'}}
            >
              <button
                type="button"
                className="expand-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleBookingInfoExpansion();
                }}
                aria-label={expandedBookingInfo ? 'Collapse booking information' : 'Expand booking information'}
              >
                <span className={`expand-icon ${expandedBookingInfo ? 'expanded' : ''}`}>
                  {expandedBookingInfo ? <IoChevronUp/> : <IoChevronDown/>}
                </span>
              </button>
              <h2>Booking Information</h2>
            </div>
          </div>

          {expandedBookingInfo && (
            <div className="basic-info-content">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="ground_name">
                    Ground
                  </label>
                  <div className="form-control-static">
                    {bookingData.ground_field?.ground?.name || 'N/A'}
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="ground_name">
                    Field
                  </label>
                  <div className="form-control-static">
                    {bookingData.ground_field?.name || 'N/A'}
                  </div>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="booking_date">
                    Booking Date
                  </label>
                  <div className="form-control-static">
                    {bookingData.date ? new Date(bookingData.date).toLocaleDateString() : 'N/A'}
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="booking_status">
                    Status
                  </label>
                  <div className="form-control-static">
                    <span className={`badge ${getStatusBadgeClass(bookingData.status)}`}>
                      {getStatusDisplayLabel(bookingData.status)}
                    </span>
                  </div>
                </div>
              </div>

              {/*<div className="form-row">*/}
              {/*  <div className="form-group">*/}
              {/*    <label htmlFor="booking_time">*/}
              {/*      Start Time*/}
              {/*    </label>*/}
              {/*    <div className="form-control-static">*/}
              {/*      {bookingData.start_time || 'N/A'}*/}
              {/*    </div>*/}
              {/*  </div>*/}
              {/*  <div className="form-group">*/}
              {/*    <label htmlFor="booking_time">*/}
              {/*      End Time*/}
              {/*    </label>*/}
              {/*    <div className="form-control-static">*/}
              {/*      {bookingData.end_time || 'N/A'}*/}
              {/*    </div>*/}
              {/*  </div>*/}
              {/*</div>*/}

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="booking_price">
                    Price (PKR)
                  </label>
                  <div className="form-control-static">
                    {formatPrice(Math.round(bookingData.price) || 0)}
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="payment_method">
                    Payment Method
                  </label>
                  <div className="form-control-static">
                    {getPaymentMethodDisplay(bookingData.payment_method)}
                  </div>
                </div>
              </div>

              {bookingData.reminder_sent && (
                <div className="form-group">
                  <label htmlFor="reminder_sent">
                    Reminder Status
                  </label>
                  <div className="form-control-static">
                    <span className="badge badge-info">Reminder Sent</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Challenge Information Section - Only show if it's a challenge */}
          {bookingData.is_challenge && (
            <>
              <div className="section-header">
                <div
                  className="section-title-wrapper"
                  onClick={toggleChallengeInfoExpansion}
                  style={{cursor: 'pointer'}}
                >
                  <button
                    type="button"
                    className="expand-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleChallengeInfoExpansion();
                    }}
                    aria-label={expandedChallengeInfo ? 'Collapse challenge information' : 'Expand challenge information'}
                  >
                    <span className={`expand-icon ${expandedChallengeInfo ? 'expanded' : ''}`}>
                      {expandedChallengeInfo ? <IoChevronUp/> : <IoChevronDown/>}
                    </span>
                  </button>
                  <h2>Challenge Information</h2>
                </div>
              </div>

              {expandedChallengeInfo && (
                <div className="basic-info-content">
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="game_type">
                        Game Type
                      </label>
                      <div className="form-control-static">
                        {bookingData.game_type || 'N/A'}
                      </div>
                    </div>
                    <div className="form-group">
                      <label htmlFor="total_players">
                        Total Players
                      </label>
                      <div className="form-control-static">
                        {bookingData.total_players || 'N/A'}
                      </div>
                    </div>
                  </div>

                  {bookingData.players && bookingData.players.length > 0 && (
                    <div className="form-group">
                      <label htmlFor="players">
                        Participants ({bookingData.players.length})
                      </label>
                      <div className="form-control-static" style={{border: 'none', background: 'none', padding: 0}}>
                        <div className="players-grid">
                          {bookingData.players.map((player, index) => (
                            <div key={player.id || index} className="player-card">
                              <div className="player-avatar">
                                {player.pic ? (
                                  <img 
                                    src={player.pic} 
                                    alt={`${player.full_name || 'Player'}'s profile`}
                                    className="player-image"
                                  />
                                ) : (
                                  <span className="player-initial">
                                    {(player.full_name || player.email || 'U').charAt(0).toUpperCase()}
                                  </span>
                                )}
                              </div>
                              <div className="player-info">
                                <div className="player-name">
                                  {player.full_name || 'Unknown Player'}
                                </div>
                                <div className="player-email">
                                  {player.email || 'No email provided'}
                                </div>
                              </div>
                              <div className="player-status">
                                <span className="status-badge confirmed">Confirmed</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* Booking Slots Section */}
          {bookingData.slots && bookingData.slots.length > 0 && (
            <>
              <div className="section-header">
                <div
                  className="section-title-wrapper"
                  onClick={toggleSlotsExpansion}
                  style={{cursor: 'pointer'}}
                >
                  <button
                    type="button"
                    className="expand-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleSlotsExpansion();
                    }}
                    aria-label={expandedSlots ? 'Collapse booking slots' : 'Expand booking slots'}
                  >
                    <span className={`expand-icon ${expandedSlots ? 'expanded' : ''}`}>
                      {expandedSlots ? <IoChevronUp/> : <IoChevronDown/>}
                    </span>
                  </button>
                  <h2>Booking Slots ({bookingData.slots.length})</h2>
                </div>
              </div>

              {expandedSlots && (
                <div className="basic-info-content">
                  <div className="slots-table">
                    {(() => {
                      // Group slots by date
                      const slotsByDate = {};
                      bookingData.slots.forEach(slot => {
                        const date = slot.date || bookingData.date;
                        if (!slotsByDate[date]) {
                          slotsByDate[date] = [];
                        }
                        slotsByDate[date].push(slot);
                      });

                      return Object.keys(slotsByDate).sort().map(date => (
                        <div key={date} className="date-slot-group">
                          <div className="date-header">
                            <div className="date-icon">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M8 2V5M16 2V5M3.5 9.09H20.5M21 8.5V17C21 20 19.5 22 16 22H8C4.5 22 3 20 3 17V8.5C3 5.5 4.5 3.5 8 3.5H16C19.5 3.5 21 5.5 21 8.5Z" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M15.6947 13.7H15.7037M15.6947 16.7H15.7037M11.9955 13.7H12.0045M11.9955 16.7H12.0045M8.29431 13.7H8.30329M8.29431 16.7H8.30329" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </div>
                            <div className="date-info">
                              <div className="date-primary">
                                {new Date(date).toLocaleDateString('en-US', {
                                  weekday: 'long',
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                })}
                              </div>
                            </div>
                            <div className="slots-count">
                              {slotsByDate[date].length} slot{slotsByDate[date].length > 1 ? 's' : ''}
                            </div>
                          </div>
                          <div className="slots-container">
                            <table>
                              <thead>
                              <tr>
                                <th>Time</th>
                                <th>Duration</th>
                                <th>Price (PKR)</th>
                              </tr>
                              </thead>
                              <tbody>
                              {slotsByDate[date].map((slot, index) => (
                                <tr key={index}>
                                  <td className="time-cell">
                                    <div className="time-display">
                                      {formatTime12Hour(slot.start_time)} - {formatTime12Hour(slot.end_time)}
                                    </div>
                                  </td>
                                  <td className="duration-cell">
                                    {(() => {
                                      const start = new Date(`2000-01-01T${slot.start_time}`);
                                      const end = new Date(`2000-01-01T${slot.end_time}`);
                                      const diffMs = end - start;
                                      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
                                      const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
                                      if (diffHours > 0 && diffMinutes > 0) {
                                        return `${diffHours}h ${diffMinutes}m`;
                                      } else if (diffHours > 0) {
                                        return `${diffHours}h`;
                                      } else {
                                        return `${diffMinutes}m`;
                                      }
                                    })()}
                                  </td>
                                  <td className="price-cell">
                                    <span className="price-amount">Rs. {formatPrice(slot.price)}</span>
                                  </td>
                                </tr>
                              ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              )}

              {bookingData && (
                <div className="info-section">
                  <div className="info-item">
                    <span className="info-label">Booking ID:</span>
                    <span className="info-value">{bookingData.id}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Created:</span>
                    <span className="info-value">
                    {new Date(bookingData.created).toLocaleDateString()}
                  </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Modified:</span>
                    <span className="info-value">
                    {new Date(bookingData.modified).toLocaleDateString()}
                  </span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      <ConfirmModal
        show={showConfirmModal}
        title="Confirm Booking"
        message="Are you sure you want to confirm this booking?"
        confirmText="Confirm"
        cancelText="Cancel"
        onConfirm={handleConfirmBooking}
        onCancel={() => setShowConfirmModal(false)}
        loading={actionLoading}
        variant="primary"
      />

      <ConfirmModal
        show={showDeclineModal}
        title="Decline Booking"
        message="Are you sure you want to decline this booking?"
        confirmText="Decline"
        cancelText="Cancel"
        onConfirm={handleDeclineBooking}
        onCancel={() => setShowDeclineModal(false)}
        loading={actionLoading}
        variant="danger"
      />
    </div>
  );
}

export default BookingDetail;